import { Flex } from 'rebass/styled-components';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import DataTable from 'components/molecules/agGrid';
import { convertQueryToString } from 'helpers/queryString';
import { trackEvent } from 'helpers/analytics';
import { Funnel } from 'lucide-react';
import { ToolbarButton } from 'components/molecules/agGrid/components/toolbarButton';
import useJobActions from '../../../hooks/useJobActions';
import { activeNetworkSelector, jobApplicantsJobTnSelector, userSelector } from '../../../redux/selectors';
import { isNetworkAdminSelector } from '../../../hooks/usePermissions/selectors';
import { getJobsColumns } from './jobColumns';
import { useTableRows } from './hooks/useTableRows';
import { useActionItems } from './hooks/useActionItems';
import { useJobTableFunctions } from './hooks/useJobTableFunctions';
import { ExportJobs } from './exportJobs';
import { JobTableModals } from './jobTableModals';
import { JobsSidebar } from '../jobsTableWithFilters/jobsSidebar';
import { useFindemWidget } from './hooks/useFindemWidget';
import { useFindemEvents } from './hooks/useFindemEvents';

export const JobsDataTable = forwardRef(
  (
    {
      isJobsLoading,
      isJobsInitialized,
      totalJobs,
      loadNextPage,
      history,
      jobs,
      hasMoreJobs,
      onSort,
      emptyView,
      initialSortBy,
      source,
      showListColumn,
      isCompanyProfile,
      onFiltersClicked,
      jobFilterProps,
      isSourcingAgent,
    },
    ref,
  ) => {
    const { jobActions, jobActionsLoading, jobActionsErrors, dialogStates } = useJobActions({
      source,
    });
    const [selectedJob, setSelectedJob] = useState(null);
    const location = useLocation();
    const activeNetwork = useSelector(activeNetworkSelector);
    const isNetworkAdmin = useSelector(isNetworkAdminSelector);
    const companyItemsToHide = useMemo(() => {
      const columnsToHide = isCompanyProfile ? ['visibility', 'featured'] : [];

      if (!activeNetwork.hasConnect) {
        columnsToHide.push('list');
      }

      const { features } = activeNetwork;

      if (!features.includes('sourcing_agent')) {
        columnsToHide.push('applicationBoostAgent');
      }

      return columnsToHide;
    }, [activeNetwork, isCompanyProfile]);
    const jobsColumns = getJobsColumns(new Set(showListColumn ? companyItemsToHide : ['list', ...companyItemsToHide]), {
      totalJobs,
      isLoading: isJobsLoading && !isJobsInitialized,
    });
    const {
      createMatches,
      isCreatingList,
      getJobById,
      selectAllJobs,
      onSelect,
      isJobSourcePostedActiveOrExpired,
      handleSort,
      onColumnAction,
      onShowJobRulesClick,
      showJobRules,
      setShowJobRules,
    } = useJobTableFunctions({ jobs, onSort });
    const showApplicants = useSelector(jobApplicantsJobTnSelector) && activeNetwork?.connect?.hasAccess;
    const user = useSelector(userSelector);
    const findem = useFindemWidget();
    const onLaunchApplicationBoostAgent = useCallback(
      ({ view, job }) => {
        findem.openWidget({ view, job });
      },
      [findem],
    );

    const tableRows = useTableRows({
      userEmail: user?.email,
      jobs,
      onShowJobRulesClick,
      selectedJobId: selectedJob?.id,
      createMatches,
      isCreatingList,
      onListClicked: (list) => history.push(`/networks/${activeNetwork.slug}/list/${list.id}/contacts`),
      onApplicantsClicked: (job) => {
        trackEvent('jobs:see_applicants', { jobId: job.id, numberApplicants: job.jobApplicantsCount });

        history.push(
          `/networks/${activeNetwork.slug}/contacts?${convertQueryToString({
            filters: { jobApplicationsJobs: [{ value: job.id, label: job.title }] },
          })}`,
        );
      },
      showApplicants,
      hasConnect: activeNetwork?.connect?.hasAccess,
      onLaunchApplicationBoostAgent,
    });

    const selectedJobs = useMemo(() => jobs.filter((job) => job.selected).map((job) => job.id), [jobs]);

    const actionMenuItems = useActionItems({
      jobActionsLoading,
      isJobSourcePostedActiveOrExpired,
      getJobById,
      jobActions,
      activeNetwork,
      source,
      isNetworkAdmin,
      setSelectedJob,
      isCompanyProfile,
      showApplicants,
    });

    useFindemEvents();

    const tableHeight = useMemo(() => {
      if (isSourcingAgent) {
        return source === 'employer_profile' ? 'calc(100vh - 200px)' : 'calc(100vh - 30px)';
      }
      return source === 'employer_profile' ? 'calc(100vh - 200px)' : 'calc(100vh - 100px)';
    }, [source, isSourcingAgent]);

    return (
      <Flex flexDirection="column" flexGrow="1" sx={{ zIndex: 1, position: 'relative' }}>
        <DataTable
          ref={ref}
          rightActions={<ExportJobs totalJobs={totalJobs} selectedJobs={selectedJobs} selectAllJobs={selectAllJobs} />}
          leftActions={
            <ToolbarButton
              option={{ value: 'filter', label: 'Filter', icon: Funnel }}
              toolbarOptionChange={onFiltersClicked}
              active={Boolean(jobFilterProps?.showFilters)}
              toolbarCount={jobFilterProps?.filtersCount || 0}
            />
          }
          useHorizontalActions={false}
          fixedColumn="first"
          totalItems={totalJobs}
          canSort
          canSelect={isNetworkAdmin}
          onSelect={onSelect}
          selectedAll={selectAllJobs}
          initialSortBy={initialSortBy}
          onSort={handleSort}
          loadMore={loadNextPage}
          items={tableRows}
          columnDefinition={jobsColumns}
          hasMore={hasMoreJobs}
          isInitialized={isJobsInitialized}
          isLoadingMore={isJobsInitialized && isJobsLoading}
          emptyView={emptyView}
          onColumnAction={onColumnAction}
          persistColumnKey={`${activeNetwork.id}-${source}-${isCompanyProfile ? 'company-profile' : 'companies'}-v7`}
          gridOptions={{
            suppressRowClickSelection: false,
            cellSelection: true,
            animateRows: false,
            processCellForClipboard: (params) => params.formatValue(),
            rowClassRules: {
              'ai-campaign-row': ({ data }) =>
                Boolean(data.original.sourcingCampaign?.status) &&
                data.original.sourcingCampaign?.status === 'in_progress' &&
                activeNetwork.features.includes('sourcing_agent'),
            },
          }}
          rowActions={{
            height: 330,
            items: actionMenuItems,
          }}
          sx={{
            height: tableHeight,
          }}
          filterSx={{
            maxHeight: tableHeight,
          }}
          cellStyle={{
            padding: '12px 8px',
          }}
          FiltersComponent={JobsSidebar}
          companyFilters={jobFilterProps}
        />
        <JobTableModals
          dialogStates={dialogStates}
          selectedJob={selectedJob}
          jobActions={jobActions}
          jobActionsLoading={jobActionsLoading}
          jobActionsErrors={jobActionsErrors}
          isNetworkAdmin={isNetworkAdmin}
          activeNetwork={activeNetwork}
          location={location}
          showJobRules={showJobRules}
          setShowJobRules={setShowJobRules}
          findem={findem}
        />
      </Flex>
    );
  },
);

JobsDataTable.propTypes = {
  isJobsLoading: PropTypes.bool,
  isJobsInitialized: PropTypes.bool,
  totalJobs: PropTypes.number,
  loadNextPage: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  jobs: PropTypes.array,
  hasMoreJobs: PropTypes.bool,
  onSort: PropTypes.func,
  emptyView: PropTypes.node,
  initialSortBy: PropTypes.shape({
    id: PropTypes.string.isRequired,
    desc: PropTypes.bool.isRequired,
  }),
  source: PropTypes.string,
  showListColumn: PropTypes.bool,
  isCompanyProfile: PropTypes.bool,
  onFiltersClicked: PropTypes.func,
  jobFilterProps: PropTypes.object,
  isSourcingAgent: PropTypes.bool,
};

JobsDataTable.defaultProps = {
  isJobsLoading: false,
  isJobsInitialized: false,
  totalJobs: 0,
  jobs: [],
  hasMoreJobs: false,
  onSort: () => {},
  emptyView: null,
  initialSortBy: { id: 'expirationDate', desc: false },
  source: 'jobs',
  showListColumn: false,
  isSourcingAgent: false,
  isCompanyProfile: false,
  onFiltersClicked: () => {},
  jobFilterProps: {},
};
